import { A11yModule } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import { AsyncPipe, LowerCasePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { ExternalPortal, GlobalNavData } from '@shared/models/global-nav.model';
import { MenuList } from '@shared/models/menu-list';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { PreloginService } from '@shared/services/prelogin.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { MtlsPricelessButtonComponent } from '@shared/shared/components/mtls-priceless-button/mtls-priceless-button.component';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { EligibilitiesAndSelfRegEnum } from '@shared/shared/enums/eligibilities-and-selfregs.enum';
import { BaseComponent } from '@shared/shared/helpers/base-component';
import { IMemberProfile } from '@shared/shared/interfaces/member-profile.interfaces';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import { SiteMapPage, SitemapService } from '@shared/shared/services/sitemap.service';
import { SELECTED_TRAVELERS } from 'app.config';
import { NavigationService } from 'core/services/navigation.service';
import { Observable, of } from 'rxjs';
import { filter, take, withLatestFrom } from 'rxjs/operators';
import {
  ISiteListDetails,
  ISiteSettings,
  ISiteSettingsSwitches,
} from '../../../shared/shared/interfaces/site-settings.interfaces';
import { CountrySelectorComponent } from '../country-selector/country-selector.component';

@Component({
  selector: 'ten-mobile-nav',
  templateUrl: './mobile-nav.component.html',
  styleUrls: ['./mobile-nav.component.scss'],
  standalone: true,
  imports: [
    A11yModule,
    NgClass,
    NgIf,
    MatLegacyButtonModule,
    OverlayModule,
    CountrySelectorComponent,
    MatIconModule,
    AsyncPipe,
    LowerCasePipe,
    AccessibleClickDirective,
    NgFor,
    RouterModule,
    TranslateModule,
    MtlsPricelessButtonComponent,
  ],
})
export class MobileNavComponent extends BaseComponent implements OnInit {
  @Output() public closed = new EventEmitter<void>();
  @Output() public logout = new EventEmitter<void>();
  @Output() public login = new EventEmitter<void>();
  @Output() public register = new EventEmitter<void>();

  @Input() public siteSettingsLoaded: boolean;
  @Input() public showFeatureNavLinks: boolean;
  @Input() public externalPortal: ExternalPortal;
  @Input() public appFeatures: ISiteSettingsSwitches;
  @Input() public memberProfile$: Observable<IMemberProfile>;
  @Input() public menuList: MenuList;
  @Input() public isUserLoggedIn: boolean;
  @Input() public inModalView = false;
  @Input() public preloginPages = false;

  public secondaryMenuList$: Observable<MenuList[]>;
  public burgerMenuList$: Observable<MenuList[]>;
  public contentOutside$: Observable<MenuList[]>;
  public subMenuList: SiteMapPage;
  public togglePriceless = false;
  public variables$: Observable<CustomisableVariables>;
  public showPlatformProvider = false;
  public platformProviderType = '';
  public isAccessHrt: boolean;
  public componentData: GlobalNavData;
  public showSelfReg = false;
  public eligibilityType: string;
  public showSiteSelectorDropDown = false;
  public selectedSite: Observable<ISiteListDetails>;
  public sites$: Observable<ISiteListDetails[]>;
  public siteDisplayName = '';
  public isCountrySelectVisible = false;
  public positionsCountrySelectMenu = [];
  public HomepageContentOutside = false;
  public showGlobeIcon = false;

  constructor(
    private sitemapService: SitemapService,
    private navigationService: NavigationService,
    private siteSettingsService: SiteSettingsService,
    private customisableVariablesService: CustomisableVariablesService,
    private router: Router,
    private siteConfService: SiteConfigService,
    private preloginService: PreloginService
  ) {
    super(siteConfService, 'header');
    this.selectedSite = localStorage.getItem('selectedCountry')
      ? of(JSON.parse(localStorage.getItem('selectedCountry')))
      : siteConfService.selectedSite;
    this.sites$ = siteConfService.siteList;

    if (this.isUserLoggedIn) {
      this.sitemapService.siteMap$.subscribe((data) => {
        this.subMenuList = data[0];
      });
    }

    this.secondaryMenuList$ = this.navigationService.getNavigationLinks('secondary');
    this.burgerMenuList$ = this.navigationService.getNavigationLinks('burger-menu');
    this.contentOutside$ = this.navigationService.getNavigationLinks('content-outside');
  }

  public ngOnInit() {
    this.showGlobeIcon = this.siteConfService.getComponentData('header')?.default?.showGlobeNotCountryFlag;
    this.selectedSite.pipe(take(1)).subscribe((res) => {
      this.siteDisplayName = this.showGlobeIcon
        ? res?.display_name
        : res?.display_name?.substring(0, res?.display_name.indexOf('(')) + ' - ' + res?.country_code;
    });
    this.siteSettingsService
      .fetchSiteSettings()
      .pipe(
        filter(() => !this.siteSettingsService.fetchingSettings),
        withLatestFrom(this.siteConfService.selfRegSitesCount),
        take(1)
      )
      .subscribe(([data, sitesCount]: [ISiteSettings, number]) => {
        this.togglePriceless = data.switches.enable_mtls_priceless_planet_widget;
        this.showPlatformProvider = data.fe_configuration.show_platform_provider_nav;
        this.platformProviderType = data.fe_configuration.platform_provider_type;
        this.isAccessHrt = data.switches.access_to_HTR;
        this.showSiteSelectorDropDown = !data?.switches.show_country_language_selector_page && sitesCount > 1;
        this.eligibilityType = data.eligibilities_and_selfregs;
        this.showSelfReg =
          data?.eligibilities_and_selfregs === EligibilitiesAndSelfRegEnum.EXTERNAL ||
          data?.eligibilities_and_selfregs === EligibilitiesAndSelfRegEnum.NON_TM_LINK;
        this.HomepageContentOutside = data.switches.pre_login_home_page;
      });

    this.variables$ = this.customisableVariablesService.variables;
  }

  public clearSelectedTravelers(): void {
    localStorage.removeItem(SELECTED_TRAVELERS);
  }

  public updateSite(value) {
    this.selectedSite = of(value);
    if (this.preloginPages) {
      this.preloginService.eventTrack('Prelogin - Navigation Language selected', {
        language: value.language,
        country: value.country_code,
      });
    }
  }

  public navigateTo(value: MenuList): void {
    window.location.href = value?.routerLink;
    if (this.preloginPages) {
      this.preloginService.eventTrack(value.eventName);
    }
  }

  public navigate(link: string) {
    this.clearSelectedTravelers();
    this.router.navigate([link]);
    this.closed.emit();
  }
}
